import React, { createContext, HTMLAttributes, useCallback } from 'react';
import '../../base.scss';
import './CheckboxGroup.scss';
import { classNames } from '../../utilities';

export interface CheckboxGroupProps extends HTMLAttributes<HTMLInputElement> {
  children: React.ReactNode;
  /** Uncontrolled default checkbox state */
  defaultChecked?: boolean;
  /** Disable all choices */
  disabled?: boolean;
  /** Default name for each checkbox */
  name?: string;
  /**
   * Callback handler for value changes
   * const { checked, value, name } = event.target
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Use React Context to pass defaults, props and callbacks into children
 */

// grab just the props applicable to children checkboxes
export type CheckboxGroupContextProps
  = Pick<CheckboxGroupProps, 'onChange' | 'name' | 'disabled' | 'defaultChecked'>

// create the context with default values
const CheckboxGroupContext = createContext<CheckboxGroupContextProps>({
  name: 'CheckboxGroupContext',
});

export { CheckboxGroupContext };

/**
 * CheckBoxGroup manages a set of Checkbox components as children
 */
export function CheckboxGroup(props: CheckboxGroupProps) {
  const { children, className, defaultChecked, disabled, name, onChange } = props;

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }
    },
    [onChange]
  );

  // hook for passing context values to checkbox
  const group = React.useMemo(
    () => ({
      defaultChecked,
      disabled,
      name,
      onChange: onChangeWrapper,
    }),
    [defaultChecked, disabled, name, onChangeWrapper]
  );

  const checkboxGroupClassNames = classNames('ps-checkbox-group', className);

  return (
    <CheckboxGroupContext.Provider value={group}>
      <div className={checkboxGroupClassNames}>{children}</div>
    </CheckboxGroupContext.Provider>
  );
}
